import type {
  FinancialSituationResult,
  InvestmentCapacityVariantConfig,
  LiquidityBufferMode,
} from "@repo/shared/types";
import type { ReactNode } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { cn } from "@/lib/utils";
import { T } from "./capacity-tab.i18n";
import { type FormatNumberFn, formatCurrency, type ParseNumberFn } from "./utils";

export type TriangleField = "income" | "expenses" | "surplus";
/** QL-127 — the two typed sides of the annual-frequency triangle. */
export type AnnualField = "income" | "expenses";

type CapacityTabProps = {
  disabled: boolean;
  currency: string;
  config: InvestmentCapacityVariantConfig;
  monthlyIncome?: number;
  monthlyExpenses?: number;
  monthlySurplus?: number;
  /** QL-127 — the typed sides when the variant takes annual values. */
  annualIncome?: number;
  annualExpenses?: number;
  liquidityBuffer?: number;
  /** QL-127 — the effective mode, as the engine resolved it. */
  bufferMode: LiquidityBufferMode;
  shortTermExpenses?: number;
  /**
   * Live-computed by the widget with the shared engine: the effective triangle
   * (a side taken from the financial situation's entries is flagged
   * `*FromEntries` and shown read-only), the buffer minimum, the below-minimum
   * and missing-custom-buffer flags, the pool and the available amount.
   */
  capacity: NonNullable<FinancialSituationResult["capacity"]>;
  /**
   * QL-140 — Group clients only, and only while the per-member floor is the
   * binding term: explains that the minimum shown above is the configured
   * floor multiplied by the number of group members.
   */
  memberScaling?: { memberCount: number; floor: number; total: number } | null;
  /**
   * QL-107 — the model's fixed buffer when the client's configured flag is
   * ticked, else null. When set it IS the buffer: the input is read-only, the
   * mode choice is hidden, and the entered figure is ignored (but kept).
   */
  fixedBuffer: number | null;
  onTriangleChange: (field: TriangleField, value: number | undefined) => void;
  onAnnualChange: (field: AnnualField, value: number | undefined) => void;
  onBufferChange: (value: number | undefined) => void;
  onBufferModeChange: (mode: LiquidityBufferMode) => void;
  onShortTermChange: (value: number | undefined) => void;
};

function formatOptional(value: number | null | undefined, fmt: FormatNumberFn): string {
  if (value == null) return "";
  return fmt(value, { maximumFractionDigits: 0 });
}

/** Integer-only inputs; parseNumber handles grouping and the U+2212 minus. */
function parseOptional(raw: string, parse: ParseNumberFn): number | undefined {
  const parsed = parse(raw);
  return parsed == null ? undefined : Math.round(parsed);
}

/**
 * QL-107 — one line of the capacity form: operator | label (+ help and any
 * sub-lines) | value. The operator column is present on every row, blank
 * included, so labels and inputs stay aligned down the whole form.
 * QL-127 — `wide` lets the value column grow for the buffer mode cards.
 */
function CapacityRow({
  operator,
  label,
  help,
  emphasized = false,
  wide = false,
  children,
  sublines,
}: {
  operator?: string;
  label: string;
  help?: string;
  emphasized?: boolean;
  wide?: boolean;
  children: ReactNode;
  sublines?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid items-start gap-x-3",
        wide ? "grid-cols-[1.25rem_1fr_auto]" : "grid-cols-[1.25rem_1fr_10rem]",
      )}
    >
      <span className="py-1.5 text-right text-sm text-muted-foreground">{operator}</span>
      <div className="space-y-0.5">
        <Label className={`block py-1.5 text-sm ${emphasized ? "font-medium" : ""}`}>{label}</Label>
        {help && <p className="text-xs text-muted-foreground">{help}</p>}
        {sublines}
      </div>
      {children}
    </div>
  );
}

// QL-107 — readOnly, not disabled: `disabled` in this widget means "the flow
// is locked", and a figure that follows from other data must stay selectable.
const READ_ONLY_INPUT = "bg-muted text-muted-foreground";
const CUSTOM_BUFFER_ERROR_ID = "capacity-buffer-custom-error";

export function CapacityTab({
  disabled,
  currency,
  config,
  monthlyIncome,
  monthlyExpenses,
  monthlySurplus,
  annualIncome,
  annualExpenses,
  liquidityBuffer,
  bufferMode,
  shortTermExpenses,
  capacity,
  memberScaling = null,
  fixedBuffer,
  onTriangleChange,
  onAnnualChange,
  onBufferChange,
  onBufferModeChange,
  onShortTermChange,
}: CapacityTabProps) {
  const { formatNumber, parseNumber } = useRegionalFormat();
  const t = useT(T);

  // QL-127 — a side the financial situation's entries supply is shown from
  // the engine (which already applied it) and cannot be typed over.
  const annual = config.inputFrequency === "annual";
  const incomeLocked = capacity.incomeFromEntries === true;
  const expensesLocked = capacity.expensesFromEntries === true;
  const bothLocked = incomeLocked && expensesLocked;
  const customBuffer = fixedBuffer === null && bufferMode === "custom";
  // The two custom-buffer errors are mutually exclusive (no amount vs. an
  // amount below the minimum), so one alert element serves both.
  const customBufferInvalid =
    customBuffer && (capacity.liquidityBufferMissing === true || capacity.bufferBelowMinimum);

  const hasDeficit = capacity.monthlySurplus !== null && capacity.monthlySurplus < 0;
  const insufficientAssets =
    capacity.investablePool - capacity.liquidityBuffer - capacity.shortTermExpenses < 0;

  const amountInput = (
    value: number | null | undefined,
    onChange: ((value: number | undefined) => void) | null,
  ) => (
    <Input
      type="text"
      inputMode="numeric"
      value={formatOptional(value, formatNumber)}
      onChange={onChange ? (e) => onChange(parseOptional(e.target.value, parseNumber)) : undefined}
      disabled={disabled}
      readOnly={onChange === null}
      placeholder={onChange ? t.amountPlaceholder : undefined}
      className={cn("h-8 text-right", onChange === null && READ_ONLY_INPUT)}
    />
  );

  // Annual mode: the typed sides are annual, a locked side is the engine's
  // monthly figure × 12, and the surplus is always derived. Monthly mode: a
  // locked side is the engine's figure; the surplus is typed only while a
  // side is free to be derived from it.
  const incomeRow = annual
    ? amountInput(
        incomeLocked ? (capacity.monthlyIncome ?? 0) * 12 : annualIncome,
        incomeLocked ? null : (v) => onAnnualChange("income", v),
      )
    : amountInput(
        incomeLocked ? capacity.monthlyIncome : monthlyIncome,
        incomeLocked ? null : (v) => onTriangleChange("income", v),
      );
  const expensesRow = annual
    ? amountInput(
        expensesLocked ? (capacity.monthlyExpenses ?? 0) * 12 : annualExpenses,
        expensesLocked ? null : (v) => onAnnualChange("expenses", v),
      )
    : amountInput(
        expensesLocked ? capacity.monthlyExpenses : monthlyExpenses,
        expensesLocked ? null : (v) => onTriangleChange("expenses", v),
      );
  // A derived surplus is the engine's figure. In annual mode, or when both
  // sides are locked, it is read-only. With one side locked it stays typeable
  // (the handler derives the free side from it) but shows the engine's figure,
  // so it never goes stale when the locked side's entries change — and never
  // flips read-only mid-keystroke, which a "read-only once derived" rule did.
  const surplusDerived = annual || bothLocked;
  const oneSideLocked = incomeLocked || expensesLocked;
  const surplusRow = surplusDerived
    ? amountInput(capacity.monthlySurplus, null)
    : amountInput(oneSideLocked ? capacity.monthlySurplus : monthlySurplus, (v) =>
        onTriangleChange("surplus", v),
      );

  const bufferModeCard = (mode: LiquidityBufferMode, label: string, children: ReactNode) => {
    const id = `capacity-buffer-${mode}`;
    const selected = bufferMode === mode;
    return (
      <label
        htmlFor={id}
        className={cn(
          "flex w-36 cursor-pointer flex-col gap-1.5 rounded-lg border p-2.5",
          selected ? "border-primary ring-1 ring-primary" : "border-input",
          // Dim the card's own text only: the radio and the input inside carry
          // their own disabled styling, and stacking opacities would halve it.
          disabled && "cursor-not-allowed text-muted-foreground",
        )}
      >
        <span className="flex items-center gap-2 text-sm">
          <RadioGroupItem value={mode} id={id} />
          {label}
        </span>
        {children}
      </label>
    );
  };

  return (
    <div className="max-w-md space-y-4">
      {/* Income / expenses / surplus triangle */}
      <div className="space-y-2">
        <h4 className="text-base font-medium">
          {annual ? t.triangleHeadingAnnual : t.triangleHeading}
        </h4>
        <p className="text-sm text-muted-foreground">
          {bothLocked ? t.triangleHelpFromEntries : annual ? t.triangleHelpAnnual : t.triangleHelp}
        </p>
        <div className="space-y-2 pt-1">
          <CapacityRow label={annual ? t.annualIncome : t.monthlyIncome}>{incomeRow}</CapacityRow>
          <CapacityRow operator="−" label={annual ? t.annualExpenses : t.monthlyExpenses}>
            {expensesRow}
          </CapacityRow>
          <CapacityRow operator="=" label={t.monthlySurplus}>
            {surplusRow}
          </CapacityRow>
        </div>
        {hasDeficit && <p className="text-sm text-destructive">{t.deficitWarning}</p>}
      </div>

      {/* Deductions — liquidity buffer and short-term expenses */}
      <div className="space-y-2 border-t pt-4">
        {fixedBuffer !== null ? (
          <CapacityRow
            label={t.liquidityBuffer}
            help={t.bufferHelp}
            sublines={<p className="text-xs text-muted-foreground">{t.fixedBufferExplanation}</p>}
          >
            {amountInput(fixedBuffer, null)}
          </CapacityRow>
        ) : (
          <>
            {/* QL-127 — Minimum uses the calculated minimum; Custom uses the
                entered amount, which must reach the minimum. Typing an amount
                into the custom box is choosing it — focus alone is not, since
                Tab lands there from the checked radio (Radix keeps one tab
                stop per group) and an empty custom buffer blocks completion. */}
            <CapacityRow
              label={t.liquidityBuffer}
              help={t.bufferHelp}
              wide
              sublines={
                memberScaling && (
                  <p className="text-xs text-muted-foreground">
                    {t.bufferMemberScaling({
                      count: String(memberScaling.memberCount),
                      floor: formatCurrency(memberScaling.floor, currency, formatNumber),
                      total: formatCurrency(memberScaling.total, currency, formatNumber),
                    })}
                  </p>
                )
              }
            >
              <RadioGroup
                value={bufferMode}
                disabled={disabled}
                onValueChange={(value) => onBufferModeChange(value as LiquidityBufferMode)}
                className="flex gap-2"
              >
                {bufferModeCard(
                  "minimum",
                  t.bufferMinimum,
                  <span
                    className={cn(
                      "py-1 text-sm",
                      // Struck only while the custom amount is the one in use: below
                      // the minimum (or empty) the engine falls back to the minimum.
                      customBuffer && !customBufferInvalid && "text-muted-foreground line-through",
                    )}
                  >
                    {formatCurrency(capacity.bufferMinimum, currency, formatNumber)}
                  </span>,
                )}
                {bufferModeCard(
                  "custom",
                  t.bufferCustom,
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={formatOptional(liquidityBuffer, formatNumber)}
                    onChange={(e) => {
                      const next = parseOptional(e.target.value, parseNumber);
                      onBufferChange(next);
                      if (!customBuffer && next !== undefined) onBufferModeChange("custom");
                    }}
                    disabled={disabled}
                    placeholder={t.enterAmount}
                    // The wrapping <label> names the radio, not this box.
                    aria-label={`${t.liquidityBuffer} — ${t.bufferCustom}`}
                    aria-invalid={customBufferInvalid ? true : undefined}
                    aria-describedby={customBufferInvalid ? CUSTOM_BUFFER_ERROR_ID : undefined}
                    className="h-8 text-right"
                  />,
                )}
              </RadioGroup>
            </CapacityRow>
            {customBufferInvalid && (
              <p id={CUSTOM_BUFFER_ERROR_ID} role="alert" className="text-sm text-destructive">
                {capacity.liquidityBufferMissing
                  ? t.customBufferMissing
                  : t.customBufferBelowMinimum(
                      formatCurrency(capacity.bufferMinimum, currency, formatNumber),
                    )}
              </p>
            )}
          </>
        )}

        {config.shortTermExpensesEnabled && (
          <CapacityRow label={t.shortTermExpenses} help={t.shortTermHelp}>
            {amountInput(shortTermExpenses, onShortTermChange)}
          </CapacityRow>
        )}
      </div>

      {/* Available for investments */}
      <div className="space-y-1 border-t pt-4">
        <CapacityRow label={t.availableForInvestments} help={t.availableHelp} emphasized>
          <p className="py-0.5 text-right text-lg font-semibold text-green-600">
            {formatCurrency(capacity.availableForInvestments, currency, formatNumber)}
          </p>
        </CapacityRow>
        {insufficientAssets && <p className="text-sm text-destructive">{t.insufficientAssets}</p>}
      </div>
    </div>
  );
}
